import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  AlertCircle,
  Briefcase,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Search,
  Trash2,
  Edit2,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import Modal from '../components/ui/Modal'
import {
  type CourtAgentDto,
  type CourtAgentListResponse,
  type CreateCourtAgentInput,
} from '@toga/shared'
import { useAuth } from '../contexts/AuthContext'

export default function Procurators() {
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')

  const [items, setItems] = useState<CourtAgentDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<CourtAgentDto | 'new' | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      const { items } = await api.get<CourtAgentListResponse>(
        `/court-agents?${params.toString()}`
      )
      setItems(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [q])

  useEffect(() => {
    void reload()
  }, [reload])

  const onDelete = async (a: CourtAgentDto) => {
    if (!confirm(`¿Eliminar a ${a.fullName}?`)) return
    try {
      await api.delete(`/court-agents/${a.id}`)
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      alert(body?.error ?? 'Error')
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Briefcase size={20} className="text-toga-300" />
            Procuradores
          </h1>
          <p className="text-xs t-muted">{items.length} dado(s) de alta</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> Nuevo procurador
          </button>
        )}
      </header>

      <div className="surface p-3">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, NIF, partido judicial…"
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-muted flex items-center gap-2 text-sm">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="surface p-8 text-center">
          <Briefcase size={32} className="mx-auto t-faint mb-2" />
          <p className="text-sm t-muted">Aún no has dado de alta ningún procurador.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((a) => (
            <li key={a.id} className="surface p-4 space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold t-primary truncate">{a.fullName}</div>
                  {a.collegeName && (
                    <div className="text-xs t-muted">
                      {a.collegeName}
                      {a.collegeNumber ? ` · n.º ${a.collegeNumber}` : ''}
                    </div>
                  )}
                </div>
                {canWrite && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setEditing(a)}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-300"
                      title="Editar"
                    >
                      <Edit2 size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDelete(a)}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                      title="Eliminar"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
              {a.judicialDistrict && (
                <div className="text-xs t-secondary flex items-center gap-1.5">
                  <MapPin size={11} className="t-faint" />
                  {a.judicialDistrict}
                </div>
              )}
              {a.email && (
                <div className="text-xs t-secondary flex items-center gap-1.5 truncate">
                  <Mail size={11} className="t-faint" />
                  <a href={`mailto:${a.email}`} className="hover:underline truncate">
                    {a.email}
                  </a>
                </div>
              )}
              {(a.phone || a.mobile) && (
                <div className="text-xs t-secondary flex items-center gap-1.5">
                  <Phone size={11} className="t-faint" />
                  {a.phone ?? a.mobile}
                </div>
              )}
              <div className="text-[10px] t-faint border-t border-base/40 pt-1.5">
                {a.activeCasesCount} expediente{a.activeCasesCount !== 1 ? 's' : ''} activo
                {a.activeCasesCount !== 1 ? 's' : ''}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ProcuratorFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function ProcuratorFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: CourtAgentDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const isEdit = initial !== null
  const [form, setForm] = useState<CreateCourtAgentInput>({
    fullName: initial?.fullName ?? '',
    legalName: initial?.legalName ?? null,
    nationalId: initial?.nationalId ?? null,
    collegeName: initial?.collegeName ?? null,
    collegeNumber: initial?.collegeNumber ?? null,
    judicialDistrict: initial?.judicialDistrict ?? null,
    email: initial?.email ?? null,
    phone: initial?.phone ?? null,
    mobile: initial?.mobile ?? null,
    address: initial?.address ?? null,
    city: initial?.city ?? null,
    postalCode: initial?.postalCode ?? null,
    province: initial?.province ?? null,
    notes: initial?.notes ?? null,
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upd = <K extends keyof CreateCourtAgentInput>(k: K, v: CreateCourtAgentInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.fullName.trim()) {
      setError('El nombre es obligatorio')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const payload = { ...form, email: form.email || null }
      if (isEdit && initial) {
        await api.patch(`/court-agents/${initial.id}`, payload)
      } else {
        await api.post('/court-agents', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Editar procurador' : 'Nuevo procurador'}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className="text-xs t-muted">Nombre completo *</span>
            <input
              value={form.fullName}
              onChange={(e) => upd('fullName', e.target.value)}
              className={input}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Razón social</span>
            <input
              value={form.legalName ?? ''}
              onChange={(e) => upd('legalName', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">NIF/CIF</span>
            <input
              value={form.nationalId ?? ''}
              onChange={(e) => upd('nationalId', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Colegio</span>
            <input
              value={form.collegeName ?? ''}
              onChange={(e) => upd('collegeName', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">N.º colegiado</span>
            <input
              value={form.collegeNumber ?? ''}
              onChange={(e) => upd('collegeNumber', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">Partido judicial</span>
            <input
              value={form.judicialDistrict ?? ''}
              onChange={(e) => upd('judicialDistrict', e.target.value || null)}
              placeholder="Madrid, Barcelona, Sevilla…"
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Email</span>
            <input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => upd('email', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Teléfono</span>
            <input
              value={form.phone ?? ''}
              onChange={(e) => upd('phone', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Móvil</span>
            <input
              value={form.mobile ?? ''}
              onChange={(e) => upd('mobile', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">Dirección</span>
            <input
              value={form.address ?? ''}
              onChange={(e) => upd('address', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Ciudad</span>
            <input
              value={form.city ?? ''}
              onChange={(e) => upd('city', e.target.value || null)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">C.P. / Provincia</span>
            <div className="flex gap-2">
              <input
                value={form.postalCode ?? ''}
                onChange={(e) => upd('postalCode', e.target.value || null)}
                className={input}
                style={{ width: '40%' }}
              />
              <input
                value={form.province ?? ''}
                onChange={(e) => upd('province', e.target.value || null)}
                className={input}
              />
            </div>
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">Notas</span>
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => upd('notes', e.target.value || null)}
              rows={2}
              className={input}
            />
          </label>
        </div>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
